/**
 * Why the system rates an instrument the way it does.
 *
 * Every figure here is the server's, including the contribution. The browser
 * formats and never multiplies: recomputing a factor against its weight would
 * be a second opinion about a decision that has already been taken.
 */
import { UNREPORTED, type ScoreContribution } from "@/shared/strategy/score";

const ABSENT = "—";

const FACTOR_LABEL: Record<string, string> = {
  recovery_rate: "Доля восстановлений",
  recovery_speed: "Скорость восстановления",
  decline_stability: "Устойчивость снижения",
  net_per_capital_day: "NET на капитало-день",
  pullback_frequency: "Частота откатов",
};

export function ScoreBreakdown({
  symbol,
  contributions,
}: {
  readonly symbol: string;
  readonly contributions: readonly ScoreContribution[];
}): React.JSX.Element | null {
  if (contributions.length === 0) return null;

  return (
    <details data-testid={`score-breakdown-${symbol}`} className="mt-1 min-w-0">
      <summary className="cursor-pointer text-helper-sm text-text-muted hover:text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring">
        из чего сложился скор
      </summary>
      <div className="mt-1 overflow-x-auto">
        <table className="w-full min-w-[30rem] border-collapse text-helper-sm">
          <thead>
            <tr className="border-b border-border-subtle text-label-caps text-text-muted">
              <th className="py-1 pr-3 text-left font-medium">Фактор</th>
              <th className="px-3 py-1 text-right font-medium">Значение</th>
              <th className="px-3 py-1 text-right font-medium">Нормализовано</th>
              <th className="px-3 py-1 text-right font-medium">Вес</th>
              <th className="py-1 pl-3 text-right font-medium">Вклад</th>
            </tr>
          </thead>
          <tbody>
            {contributions.map((one) => (
              <tr
                key={one.key}
                data-testid={`contribution-${symbol}-${one.key}`}
                className="border-b border-border-subtle last:border-b-0"
              >
                <td className="py-1 pr-3 text-text-secondary">
                  {FACTOR_LABEL[one.key] ?? one.key}
                </td>
                <td className="px-3 py-1 text-right tabular-nums text-text-secondary">
                  {one.raw_value ?? ABSENT}
                </td>
                <td className="px-3 py-1 text-right tabular-nums text-text-secondary">
                  {one.normalized_value ?? ABSENT}
                </td>
                <td className="px-3 py-1 text-right tabular-nums text-text-secondary">
                  {one.weight ?? ABSENT}
                </td>
                <td className="py-1 pl-3 text-right tabular-nums text-text-primary">
                  {one.contribution ?? UNREPORTED}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}
